import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Upload, FileUp, X } from "lucide-react";
// xlsx loaded dynamically to reduce bundle size
import { useToast } from "@/hooks/use-toast";
import type { ResultContext } from "./ResultSetup";

export interface ParsedRow {
  sn: number;
  studentId: string;
  studentName: string;
  formIvIndex: string;
  scores: Record<string, number | string>;
  gpa: string;
  remarks: string;
  modulesToRetake: string;
  errors: string[];
}

export interface ParsedResult {
  rows: ParsedRow[];
  moduleCodes: string[];
  metadata: Record<string, string>;
  totalErrors: number;
}

interface Props {
  context: ResultContext;
  onParsed: (result: ParsedResult) => void;
}

export function BulkUpload({ context, onParsed }: Props) {
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const isReady = context.semester && context.selectedClass && context.examType && context.academicYear;

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  const handleUpload = async () => {
    if (!file || !isReady) return;
    setParsing(true);
    try {
      const XLSX = await import("xlsx");
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);

      // Validate metadata
      const metaSheet = wb.Sheets["Metadata"];
      const metadata: Record<string, string> = {};
      if (metaSheet) {
        const metaData = XLSX.utils.sheet_to_json<Record<string, string>>(metaSheet);
        metaData.forEach((row) => {
          if (row.Field && row.Value) metadata[row.Field] = row.Value;
        });
      }

      // Check context match — normalize for whitespace/casing (e.g. "CMT 4" vs "CMT4")
      const normCls = (v: string) => String(v ?? "").replace(/\s+/g, "").toUpperCase().trim();
      const normSem = (v: string) => String(v ?? "").trim().toUpperCase();
      if (metadata["Class"] && normCls(metadata["Class"]) !== normCls(context.selectedClass)) {
        toast({ title: "Class mismatch", description: `Template is for ${metadata["Class"]} but you selected ${context.selectedClass}`, variant: "destructive" });
        setParsing(false);
        return;
      }
      if (metadata["Semester"] && normSem(metadata["Semester"]) !== normSem(context.semester)) {
        toast({ title: "Semester mismatch", description: `Template is for Semester ${metadata["Semester"]} but you selected ${context.semester}`, variant: "destructive" });
        setParsing(false);
        return;
      }

      const resultsSheet = wb.Sheets["Results"] || wb.Sheets[wb.SheetNames[0]];
      if (!resultsSheet) {
        toast({ title: "Invalid template", description: "No Results sheet found", variant: "destructive" });
        setParsing(false);
        return;
      }

      // Auto-detect header row (re-upload templates have a Legend banner on row 1)
      const aoa = XLSX.utils.sheet_to_json<any[]>(resultsSheet, { header: 1, blankrows: false, defval: "" });
      const headerRowIndex = aoa.findIndex((row) => {
        if (!Array.isArray(row)) return false;
        const cells = row.map((c) => String(c ?? "").trim().toLowerCase());
        const hasSn = cells.includes("sn");
        const hasIdent = cells.includes("student id") || cells.includes("form iv index");
        return hasSn && hasIdent;
      });
      if (headerRowIndex === -1) {
        toast({ title: "Invalid template", description: "Could not locate header row (expected SN / Student ID / Form IV Index).", variant: "destructive" });
        setParsing(false);
        return;
      }
      const rawRows = XLSX.utils.sheet_to_json<Record<string, any>>(resultsSheet, { range: headerRowIndex, defval: "" });
      if (rawRows.length === 0) {
        toast({ title: "Empty template", description: "No data rows found", variant: "destructive" });
        setParsing(false);
        return;
      }

      // Normalize row keys: trim whitespace from all column headers
      const normalizedRows = rawRows.map(raw => {
        const normalized: Record<string, any> = {};
        for (const [key, value] of Object.entries(raw)) {
          normalized[key.trim()] = value;
        }
        return normalized;
      });

      // Extract module codes (columns that aren't standard)
      const standardCols = new Set(["SN", "Student ID", "Student Name", "Form IV Index", "GPA", "Gpa", "gpa", "Remarks", "Modules to Retake"]);
      const allCols = Object.keys(normalizedRows[0]);
      const moduleCodes = allCols
        .filter((c) => !standardCols.has(c) && !c.startsWith("__") && c.length > 0);

      // Helper: case-insensitive access for GPA
      const getGpa = (row: Record<string, any>) => row["GPA"] ?? row["Gpa"] ?? row["gpa"] ?? "";

      // Parse and validate rows
      const seenIds = new Set<string>();
      let totalErrors = 0;

      const rows: ParsedRow[] = normalizedRows.map((raw, idx) => {
        const errors: string[] = [];
        const formIvIndex = String(raw["Form IV Index"] ?? "").trim();
        const rawStudentId = String(raw["Student ID"] ?? "").trim();
        const studentId = formIvIndex || rawStudentId;
        const studentName = String(raw["Student Name"] ?? "").trim();

        if (!studentId) errors.push("Missing Student ID / Form IV Index");
        if (seenIds.has(studentId) && studentId) errors.push("Duplicate Student ID");
        if (studentId) seenIds.add(studentId);

        const scores: Record<string, number | string> = {};
        const validGrades = new Set(["A", "A+", "A-", "B", "B+", "B-", "C", "C+", "C-", "D", "D+", "D-", "E", "F", "I", "W", "N/A", "ABS", "SUPP", "INC"]);
        moduleCodes.forEach((code) => {
          const val = raw[code];
          if (val === undefined || val === null || val === "") {
            scores[code] = "";
          } else {
            const strVal = String(val).trim().toUpperCase();
            if (validGrades.has(strVal)) {
              scores[code] = strVal;
            } else {
              const num = Number(val);
              if (isNaN(num)) {
                errors.push(`Invalid score for ${code}: "${val}"`);
                scores[code] = String(val);
              } else if (num < 0 || num > 100) {
                errors.push(`Score out of range for ${code}: ${num}`);
                scores[code] = num;
              } else {
                scores[code] = num;
              }
            }
          }
        });

        totalErrors += errors.length;
        return {
          sn: idx + 1,
          studentId,
          studentName,
          formIvIndex,
          scores,
          gpa: String(getGpa(raw)),
          remarks: String(raw["Remarks"] ?? ""),
          modulesToRetake: String(raw["Modules to Retake"] ?? ""),
          errors,
        };
      });

      onParsed({ rows, moduleCodes, metadata, totalErrors });

      // Warn (don't block) on suspicious Form IV Index formats (e.g. "Np..." typo for "Ns...")
      const fivPattern = /^N?S\d{3,4}\/\d{3,4}\/\d{4}$/i;
      const suspicious = rows
        .filter((r) => r.formIvIndex && !fivPattern.test(r.formIvIndex.trim()))
        .map((r) => `${r.studentName || "?"} (${r.formIvIndex})`);
      if (suspicious.length > 0) {
        toast({
          title: `⚠ ${suspicious.length} Form IV Index inaonekana batili`,
          description: `Rekebisha kabla ya import ili wanafunzi hawa wapatikane: ${suspicious.slice(0, 3).join("; ")}${suspicious.length > 3 ? `; +${suspicious.length - 3} zaidi` : ""}`,
        });
      }

      toast({ title: "File parsed", description: `${rows.length} rows processed, ${totalErrors} errors found` });
    } catch (e: any) {
      toast({ title: "Parse error", description: e.message, variant: "destructive" });
    } finally {
      setParsing(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Upload className="h-4 w-4 text-primary" />
          Bulk Upload
        </CardTitle>
        <CardDescription>Upload a completed result template for validation</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3 flex-wrap">
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFile} />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={!isReady}>
            <FileUp className="mr-2 h-4 w-4" />
            {file ? file.name : "Choose File"}
          </Button>
          {file && (
            <Button variant="ghost" size="icon" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }}>
              <X className="h-4 w-4" />
            </Button>
          )}
          <Button onClick={handleUpload} disabled={!file || !isReady || parsing} className="ml-auto">
            {parsing ? "Parsing..." : "Validate & Import"}
          </Button>
        </div>
        {!isReady && <p className="text-xs text-muted-foreground mt-2">Complete the Result Setup first</p>}
      </CardContent>
    </Card>
  );
}
